import React, { useState, useRef, useEffect } from 'react';
import './index.css';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
// @ts-ignore
import { v4 as uuidv4 } from 'uuid';

import axios from 'axios';


interface Slide {
    id: number;
    title: string;
    description: string;
    fileName: string;
    presentation: {
        id: number;
        title: string;
        description: string;
        status: string;
        slidesRoot: string;
        createdAt: string;
    };
    durationMs: number;
    picture: {
        id: number;
        fileName: string;
        raw: string;
    };
    status: string;
    createdAt: string;
}

interface Visit {
    id: number;
    title: string;
    description: string;
    picture: any;
    slides: Slide[];
    video: {
        raw: string;
        hls: string | null;
        status: string;
    };
    createdAt: string;
}

interface VideoPlayerProps {
    videoUrl: string;
    slides: Slide[];
    visit: Visit;
    doctorId: string;
}

interface SlideWithTime extends Slide {
    start: number;
    end: number;
}


const VideoPlayer: React.FC<VideoPlayerProps> = ({ videoUrl, slides, visit, doctorId }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [currentSlide, setCurrentSlide] = useState<SlideWithTime | null>(null);
    const [isMuted, setIsMuted] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [fadeOut, setFadeOut] = useState(false);
    const controlsTimeout = useRef<NodeJS.Timeout | null>(null);
    const [isSeeking, setIsSeeking] = useState(false);
    const progressBarRef = useRef<HTMLDivElement>(null);


    // Генерация sessionId
    const sessionIdRef = useRef<string>(uuidv4());

    // Формируем slides с временными метками
    const slidesWithTime: SlideWithTime[] = React.useMemo(() => {
        let currentStart = 0;
        return slides.map(slide => {
            const s: SlideWithTime = {
                ...slide,
                start: currentStart,
                end: currentStart + slide.durationMs,
            };
            currentStart += slide.durationMs;
            return s;
        });
    }, [slides]);

    // Сбросить таймер и запустить плавное скрытие через 5 сек.
    const handleUserActivity = () => {
        setShowControls(true);
        setFadeOut(false);
        if (controlsTimeout.current) clearTimeout(controlsTimeout.current);
        controlsTimeout.current = setTimeout(() => setFadeOut(true), 5000);
    };

    // Клик по пустой области: если панель видна — скрыть сразу, если скрыта — показать сразу
    const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            if (showControls) {
                setShowControls(false);
                setFadeOut(false);
                if (controlsTimeout.current) clearTimeout(controlsTimeout.current);
            } else {
                setShowControls(true);
                setFadeOut(false);
                handleUserActivity();
            }
        }
    };

    useEffect(() => {
        handleUserActivity();
        return () => {
            if (controlsTimeout.current) clearTimeout(controlsTimeout.current);
        };
    }, []);

    // Обновление текущего слайда
    useEffect(() => {
        if (slidesWithTime.length === 0) return;
        const slide = slidesWithTime.find(s => currentTime >= s.start && currentTime < s.end);
        setCurrentSlide(slide || null);
    }, [currentTime, slidesWithTime]);

    // Отправка статистики каждую секунду
    useEffect(() => {
        const interval = setInterval(() => {
            if (
                !videoRef.current ||
                videoRef.current.currentTime === 0 ||
                videoRef.current.ended ||
                !isPlaying
            ) {
                return;
            }
            const ms = Math.floor(videoRef.current.currentTime * 1000);
            const slide = slidesWithTime.find(s => ms >= s.start && ms < s.end);
            if (!slide) return;

            const stat = {
                sessionId: sessionIdRef.current,
                doctorId: doctorId,
                slideId: slide.id,
                slideTitle: slide.title,
                presentationId: slide.presentation.id,
                presentationTitle: slide.presentation.title,
                visitId: visit.id,
                visitTitle: visit.title,
                scenarioId: 7,
                scenarioTitle: "SmartTensin",
                fingerprint: '',
                productId: 12,
                productTitle: "SmartTensin",
                backofficeId: 1,
                backofficeTitle: "Abbott",
                time: new Date().toISOString(),
            };

            // Здесь отправка статистики (замените на ваш endpoint)
            fetch('https://stage.selfdetailing.ru/api/statistic', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(stat),
            });

            // Для отладки:
            console.log('STAT', stat);
        }, 500);

        return () => clearInterval(interval);
    }, [isPlaying, slidesWithTime, doctorId, visit]);

    // Обновление текущего времени видео
    const handleTimeUpdate = () => {
        if (videoRef.current) {
            setCurrentTime(Math.floor(videoRef.current.currentTime * 1000));
        }
    };


    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const getTimeFromPosition = (clientX: number) => {
        if (!progressBarRef.current) return 0;
        const rect = progressBarRef.current.getBoundingClientRect();
        let x = clientX - rect.left;
        x = Math.max(0, Math.min(x, rect.width));
        const percent = x / rect.width;
        const duration = slidesWithTime.length > 0 ? slidesWithTime[slidesWithTime.length - 1].end : 0;
        return percent * duration;
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsSeeking(true);
        const newTime = getTimeFromPosition(e.clientX);
        if (videoRef.current) {
            videoRef.current.currentTime = newTime / 1000;
            setCurrentTime(newTime);
        }
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!isSeeking) return;
        const newTime = getTimeFromPosition(e.clientX);
        if (videoRef.current) {
            videoRef.current.currentTime = newTime / 1000;
            setCurrentTime(newTime);
        }
    };

    const handleMouseUp = () => {
        setIsSeeking(false);
    };

    useEffect(() => {
        if (isSeeking) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isSeeking]);

    const handleTouchStart = (e: React.TouchEvent) => {
        setIsSeeking(true);
        const touch = e.touches[0];
        const newTime = getTimeFromPosition(touch.clientX);
        if (videoRef.current) {
            videoRef.current.currentTime = newTime / 1000;
            setCurrentTime(newTime);
        }
    };

    const handleTouchMove = (e: TouchEvent) => {
        if (!isSeeking) return;
        const touch = e.touches[0];
        const newTime = getTimeFromPosition(touch.clientX);
        if (videoRef.current) {
            videoRef.current.currentTime = newTime / 1000;
            setCurrentTime(newTime);
        }
    };

    const handleTouchEnd = () => {
        setIsSeeking(false);
    };

    useEffect(() => {
        if (isSeeking) {
            window.addEventListener('touchmove', handleTouchMove);
            window.addEventListener('touchend', handleTouchEnd);
        } else {
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
        }
        return () => {
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isSeeking]);

    const totalDuration = slidesWithTime.length > 0 ? slidesWithTime[slidesWithTime.length - 1].end : 0;


    return (
        <div
            id='videoplayer'
            className="video-container"
            onMouseMove={handleUserActivity}
            onMouseDown={handleUserActivity}
            onTouchStart={handleUserActivity}
            onClick={handleContainerClick}
        >
            <video
                ref={videoRef}
                poster={'https://stage.selfdetailing.ru/app/uploads/presentations/15/slides/slide08_screenshots/1754514542869.png'}
                src={videoUrl}
                controls={false}
                onTimeUpdate={handleTimeUpdate}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
            <div className={`controls-overlay${(!showControls || fadeOut) ? ' hide' : ''}`}>
                <>
                    <button
                        className="big-play-btn"
                        onClick={togglePlay}
                        aria-label={isPlaying ? "Pause video" : "Play video"}
                    >
                        {isPlaying ? <PauseIcon fontSize="inherit" /> : <PlayArrowIcon fontSize="inherit" />}
                    </button>
                    <div
                        className="progress-bar-container"
                        ref={progressBarRef}
                        onMouseDown={handleMouseDown}
                        onTouchStart={handleTouchStart}
                    >
                        <div
                            className="progress-bar-fill"
                            style={{ width: `${(currentTime / totalDuration) * 100}%` }}
                        />
                    </div>


                </>
            </div>


            <div className="stats-overlay">
                {currentSlide && (
                    <div>
                        <div>Слайд: {currentSlide.title}</div>
                        <div>Презентация: {currentSlide.presentation.title}</div>
                        <div>Визит: {visit.title}</div>
                        <div>Время: {Math.floor(currentTime / 1000)} сек</div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VideoPlayer;
